/**
 * Mach 3 deck box — procedural geometry.
 *
 * Construction, as confirmed by LDB against the physical box:
 *
 *   - The die-cut sheet is ONE cover wrapping three faces: left side (panel A,
 *     87.5 = box depth), back (panel B, 109.5 = box width), right side
 *     (panel C, 87.5). Its two notched edges finish flush with the front.
 *   - Nothing covers the front, because the front facade IS the two drawer
 *     fronts. That is why the notches read as the wavy silhouette either side
 *     of the front panels in the product photos.
 *   - The cover is ONE continuous piece running the full height. There is no
 *     separate lid and the top does NOT open -- it is fixed. Only the two
 *     compartments move, sliding forward out of the front.
 *   - The interior is split by a 4mm linen shelf that the upper compartment
 *     rests on. Upper to lower is 114:296, taken off LDB's listing photos.
 *   - Every wall is 4mm: 2mm black leatherette laminated to 2mm grey linen.
 *     Each wall is therefore built as two extrusions, so the lamination shows
 *     at every cut edge -- which is most of what you see at a notch.
 *
 * The artwork is mapped from sheet coordinates, so a pixel at (x,y) on the
 * customer's upload lands exactly where it lands on the real print.
 */
(function (root, factory) {
  if (typeof module !== 'undefined') module.exports = factory();
  else root.DeckBoxModel = factory();
}(typeof self !== 'undefined' ? self : this, function () {

  var BOX = {
    w: 109.5,          // side to side  (= panel B, = 101.6 interior + 4mm wall x2)
    d: 87.5,           // front to back (= panels A and C)
    h: 157.5,          // overall height
    wall: 4,
    wallOuter: 2,      // black leatherette
    wallInner: 2,      // grey linen
    divider: 4,        // linen shelf the upper compartment rests on
    split: 114 / 410,  // upper compartment's share of the interior height
    drawerClear: 0.8,  // slack so a compartment does not scrape the shell
    edgeRoll: 0.32,    // leather rolls over its cut edges rather than ending square.
                       // Kept below the notch profile's tightest curvature: a
                       // bevel wider than the concave radius folds the surface
                       // through itself and stripes.
    panelRound: 1.3,   // and every panel corner is radiused, not mitred
    tileMM: 24         // grain tile size, shared by every surface
  };

  /**
   * Interior stack, top to bottom: top wall, upper compartment, divider,
   * lower compartment, floor. Derived rather than hard-coded so changing the
   * ratio or the wall thickness cannot leave the parts not adding up.
   */
  function interior(box) {
    var usable = box.h - 2 * box.wall - box.divider;
    var upper = usable * box.split;
    var lower = usable - upper;
    var floorTop = -box.h / 2 + box.wall;
    return {
      usable: usable, upper: upper, lower: lower,
      lowerY: floorTop + lower / 2,
      dividerY: floorTop + lower + box.divider / 2,
      upperY: floorTop + lower + box.divider + upper / 2
    };
  }

  /** Sheet-x where each panel starts: A at 0, B at 87.5, C at 197. */
  function panelOrigins(lid) {
    var o = [0], acc = 0;
    for (var i = 0; i < lid.panels.length - 1; i++) { acc += lid.panels[i]; o.push(acc); }
    return o;
  }

  /**
   * Notched-edge samples for a side wall, clipped to [b0,b1] by the lid/body
   * split. `edgeX` is where the notches are cut from and `dir` which way the
   * cut runs, so the same profile serves a wall whose notched edge is at x=0
   * and one whose notched edge is at x=D -- which is how both side walls are
   * built without ever mirroring geometry. A negative scale would flip the
   * artwork with it, and the print is not symmetric.
   */
  function notchEdge(lid, b0, b1, edgeX, dir) {
    var pts = [], NP = lid.__profile;
    var ns = lid.notches.slice().sort(function (a, b) { return b.top - a.top; });
    pts.push([edgeX, b1]);
    for (var i = 0; i < ns.length; i++) {
      var n = ns[i];
      if (n.bottom <= b0 || n.top >= b1) continue;   // no overlap with this piece
      for (var k = NP.length - 1; k >= 0; k--) {
        var p = NP[k];
        var b = n.top + (n.bottom - n.top) * p[0];
        if (b < b0 || b > b1) continue;              // clipped by the split
        pts.push([edgeX + dir * n.depth * p[1], b]);
      }
    }
    pts.push([edgeX, b0]);
    return pts;
  }

  /**
   * Side-wall shape in (x, b-from-top). notchAtFar puts the notched edge at
   * x = D instead of x = 0.
   */
  function sideShape(THREE, lid, b0, b1, D, notchAtFar) {
    var s = new THREE.Shape();
    var edgeX = notchAtFar ? D : 0, dir = notchAtFar ? -1 : 1;
    var plainX = notchAtFar ? 0 : D;
    var fe = notchEdge(lid, b0, b1, edgeX, dir);
    s.moveTo(fe[0][0], -fe[0][1]);
    for (var i = 1; i < fe.length; i++) s.lineTo(fe[i][0], -fe[i][1]);
    s.lineTo(plainX, -b0);
    s.lineTo(plainX, -b1);
    s.closePath();
    return s;
  }

  function rectShape(THREE, w, b0, b1) {
    var s = new THREE.Shape();
    s.moveTo(0, -b0); s.lineTo(w, -b0); s.lineTo(w, -b1); s.lineTo(0, -b1); s.closePath();
    return s;
  }

  /**
   * UV generator mapping the extrusion's cap vertices back to sheet space.
   * `toSheet(x, y)` takes shape-local coords and returns [sheetX, sheetY].
   */
  /**
   * UVs for a laminated wall.
   *
   * The caps carry the print, so they map into sheet space (0..1, with the
   * material's repeat doing the tiling). The extruded side walls are the cut
   * edges and take the plain leather material instead, so they need UVs in
   * tile units -- and crucially they cannot reuse the cap mapping: on an edge
   * face two vertices share the same (x,y) and differ only in z, so a mapping
   * that ignores z collapses the quad to zero UV area and the grain samples
   * degenerately, which shows as stripes crawling along every notch.
   */
  function sheetUV(THREE, lid, toSheet, tileMM) {
    var t = tileMM || 24;
    function capUV(vertices, i) {
      var x = vertices[i * 3], y = vertices[i * 3 + 1];
      var s = toSheet(x, y);
      return new THREE.Vector2(s[0] / lid.w, 1 - s[1] / lid.h);
    }
    function edgeUV(vertices, i) {
      var x = vertices[i * 3], y = vertices[i * 3 + 1], z = vertices[i * 3 + 2];
      // a slanted projection so the coordinate advances whichever way the
      // profile happens to run, with depth on the other axis
      return new THREE.Vector2((x * 0.8 + y * 0.6) / t, z / t);
    }
    return {
      generateTopUV: function (geometry, vertices, a, b, c) {
        return [capUV(vertices, a), capUV(vertices, b), capUV(vertices, c)];
      },
      generateSideWallUV: function (geometry, vertices, a, b, c, d) {
        return [edgeUV(vertices, a), edgeUV(vertices, b), edgeUV(vertices, c), edgeUV(vertices, d)];
      }
    };
  }

  /**
   * One laminated wall: 2mm of printed leatherette with 2mm of linen behind it.
   * Returns a Group whose local +Z points OUT of the box.
   */
  /**
   * One laminated wall: printed leatherette over linen.
   *
   * Both layers are bevelled rather than cut square. Leather has no sharp
   * arrises -- it rolls over every edge -- and a hard 90 degree edge is most of
   * what makes a render read as machined furniture instead of a stitched
   * product. The bevel is taken out of the extrusion depth, not added to it, so
   * the wall stays exactly 4mm.
   */
  /** One bevelled layer, spanning z = 0..depth once the bevel is accounted for. */
  function bevelledExtrude(THREE, shape, depth, roundness, uvGen) {
    var b = Math.min(roundness, depth * 0.42);
    var opts = {
      depth: Math.max(0.02, depth - 2 * b),
      bevelEnabled: b > 0.01, bevelThickness: b, bevelSize: b,
      bevelSegments: 3, curveSegments: 8
    };
    if (uvGen) opts.UVGenerator = uvGen;
    var geo = new THREE.ExtrudeGeometry(shape, opts);
    geo.translate(0, 0, b);   // the bevel starts at -b; bring it back to 0..depth
    return geo;
  }

  function laminate(THREE, shape, uvGen, mats, box) {
    var g = new THREE.Group();
    var roll = Math.min(box.edgeRoll, box.wallOuter * 0.42);

    var mOuter = new THREE.Mesh(bevelledExtrude(THREE, shape, box.wallOuter, roll, uvGen),
                                [mats.art, mats.blackEdge]);
    var mInner = new THREE.Mesh(bevelledExtrude(THREE, shape, box.wallInner, roll * 0.55, uvGen),
                                [mats.linen, mats.linenEdge]);
    mInner.position.z = -box.wallInner;
    g.add(mInner, mOuter);
    return g;
  }

  /**
   * A box panel whose UVs are rescaled into millimetres, so a tiling grain map
   * lands at the same physical size on every face. BoxGeometry gives each face
   * 0..1 regardless of its dimensions, which would otherwise stretch the grain
   * differently on a 40mm face than on a 110mm one.
   */
  /** Centred rounded rectangle. */
  function roundedRect(THREE, w, h, r) {
    var s = new THREE.Shape();
    var x = -w / 2, y = -h / 2;
    r = Math.max(0.05, Math.min(r, w / 2 - 0.02, h / 2 - 0.02));
    s.moveTo(x + r, y);
    s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
    s.lineTo(x + w, y + h - r); s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    s.lineTo(x + r, y + h); s.quadraticCurveTo(x, y + h, x, y + h - r);
    s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
    return s;
  }

  /**
   * A panel with radiused corners and rolled edges, in place of a hard box.
   *
   * UVs are emitted in millimetres so a tiling grain map lands at the same
   * physical size on every panel -- an extruded shape carries no useful UVs of
   * its own, and BoxGeometry's 0..1 per face would stretch the grain
   * differently on a 40mm panel than on a 110mm one.
   */
  function panel(THREE, w, h, d, mat, tileMM, radius) {
    var t = tileMM || 24;
    var r = radius == null ? 1.3 : radius;
    r = Math.max(0.05, Math.min(r, w / 2 - 0.05, h / 2 - 0.05));
    var b = Math.min(r, d * 0.42);
    var uv = {
      generateTopUV: function (geometry, vertices, a, c, e) {
        return [a, c, e].map(function (i) {
          return new THREE.Vector2(vertices[i * 3] / t, vertices[i * 3 + 1] / t);
        });
      },
      // depth on one axis, so an edge quad never collapses to zero UV area
      generateSideWallUV: function (geometry, vertices, a, c, e, f) {
        return [a, c, e, f].map(function (i) {
          return new THREE.Vector2(
            (vertices[i * 3] * 0.8 + vertices[i * 3 + 1] * 0.6) / t,
            vertices[i * 3 + 2] / t
          );
        });
      }
    };
    var geo = new THREE.ExtrudeGeometry(roundedRect(THREE, w, h, r), {
      depth: Math.max(0.02, d - 2 * b),
      bevelEnabled: b > 0.01, bevelThickness: b, bevelSize: b,
      bevelSegments: 2, curveSegments: 8, UVGenerator: uv
    });
    geo.translate(0, 0, -(d / 2 - b));
    return new THREE.Mesh(geo, mat);
  }

  /**
   * An open-topped tray: black outside, linen inside, built from panels so the
   * rim shows the lamination the way the real inserts do.
   */
  /**
   * The sewn panels -- both compartment fronts and the fixed top -- carry a
   * stitch line 2mm inside their edges. It goes on as a decal rather than into
   * the leather map because those panels are solids with mm-scaled tiling UVs:
   * there is no per-panel rectangle to draw into. The plane is lifted a hair
   * off the face and its material is depth-write-free, so it cannot z-fight.
   * mats.stitchFor is supplied by the caller, which owns the canvas and the
   * thread colour; without it the box simply builds unstitched.
   */
  function stitchDecal(THREE, mats, wMM, hMM) {
    if (!mats.stitchFor) return null;
    var mat = mats.stitchFor(wMM, hMM);
    if (!mat) return null;
    var m = new THREE.Mesh(new THREE.PlaneGeometry(wMM, hMM), mat);
    m.userData.stitch = true;
    return m;
  }

  /**
   * Lays a seam on the outward face of `panelMesh`, sized and positioned from
   * that mesh's own bounds so it tracks the finished silhouette rather than the
   * nominal w/h. `axis` is the face's outward normal: 'z' for a compartment
   * front, 'y' for the top. Returned in the panel's parent space.
   */
  function faceStitch(THREE, mats, panelMesh, axis) {
    panelMesh.geometry.computeBoundingBox();
    var bb = panelMesh.geometry.boundingBox, p = panelMesh.position;
    var lift = 0.05;
    var m;
    if (axis === 'y') {
      m = stitchDecal(THREE, mats, bb.max.x - bb.min.x, bb.max.z - bb.min.z);
      if (!m) return null;
      m.rotation.x = -Math.PI / 2;
      m.position.set(p.x + (bb.max.x + bb.min.x) / 2,
                     p.y + bb.max.y + lift,
                     p.z + (bb.max.z + bb.min.z) / 2);
    } else {
      m = stitchDecal(THREE, mats, bb.max.x - bb.min.x, bb.max.y - bb.min.y);
      if (!m) return null;
      m.position.set(p.x + (bb.max.x + bb.min.x) / 2,
                     p.y + (bb.max.y + bb.min.y) / 2,
                     p.z + bb.max.z + lift);
    }
    return m;
  }
  function tray(THREE, w, h, d, mats, t) {
    var g = new THREE.Group();
    var wallT = t || 3;
    var out = mats.blackEdge, lin = mats.linen;
    // shell
    var bottom = panel(THREE, w, wallT, d, out); bottom.position.y = -h / 2 + wallT / 2; g.add(bottom);
    var back = panel(THREE, w, h, wallT, out); back.position.z = -d / 2 + wallT / 2; g.add(back);
    var front = panel(THREE, w, h, wallT, out); front.position.z = d / 2 - wallT / 2; g.add(front);
    var left = panel(THREE, wallT, h, d, out); left.position.x = -w / 2 + wallT / 2; g.add(left);
    var right = panel(THREE, wallT, h, d, out); right.position.x = w / 2 - wallT / 2; g.add(right);
    // linen lining, inset so both layers read at the rim
    var iw = w - 2 * wallT - 0.6, id = d - 2 * wallT - 0.6, ih = h - wallT - 0.6;
    var lb = panel(THREE, iw, 1.2, id, lin); lb.position.y = -h / 2 + wallT + 0.6; g.add(lb);
    var lbk = panel(THREE, iw, ih, 1.2, lin); lbk.position.set(0, 0.3, -d / 2 + wallT + 0.6); g.add(lbk);
    var lfr = panel(THREE, iw, ih, 1.2, lin); lfr.position.set(0, 0.3, d / 2 - wallT - 0.6); g.add(lfr);
    var ll = panel(THREE, 1.2, ih, id, lin); ll.position.set(-w / 2 + wallT + 0.6, 0.3, 0); g.add(ll);
    var lr = panel(THREE, 1.2, ih, id, lin); lr.position.set(w / 2 - wallT - 0.6, 0.3, 0); g.add(lr);
    // The compartment front is a sewn panel. Size and place the seam from the
    // panel's own bounds: panel() bevels OUTWARD, so the finished face is wider
    // and taller than the w/h it was asked for, and a seam sized to w/h would
    // sit a bevel too far in.
    var st = faceStitch(THREE, mats, front, 'z');
    if (st) g.add(st);
    return g;
  }

  /**
   * Builds the whole box. Returns the group plus the parts that animate.
   */
  function build(THREE, lid, mats, override) {
    var box = Object.assign({}, BOX, override || {});
    var W = box.w, D = box.d, H = box.h;
    var origins = panelOrigins(lid);
    var root = new THREE.Group();

    // ---- the cover, split into a lid piece and a body piece ----------------
    /**
     * Walls are butt-jointed, never overlapped.
     *
     * Two solids that share a face plane AND face the same way z-fight, which
     * shows as a stipple crawling along every corner as the box turns. So the
     * side walls stop one wall-thickness short of the back and the back wall is
     * narrowed by a wall at each end. Every join is then back-to-back --
     * coplanar but facing opposite ways, which backface culling resolves.
     *
     * The 4mm each side wall gives up is not lost print: that is the material
     * turning the corner, so the sheet mapping is unchanged and the back wall
     * simply starts one wall in.
     */
    function coverPiece(b0, b1) {
      var g = new THREE.Group();
      var sideD = D - box.wall;          // side walls stop short of the back
      var backW = W - 2 * box.wall;      // back sits between them

      // Each wall is placed by a pure rotation, never a mirror, so shape-x is
      // chosen per wall to make the rotation come out right.

      // LEFT wall: outward face is -X. rotateY(-90) sends local +Z to -X and
      // local +X to +Z, so shape-x runs back-to-front and the notches, which
      // belong at the front, sit at the far end of the shape.
      // Panel C, not A. The sheet is printed face-out, so wrapping it with
      // panel B as the back puts the sheet's LEFT third on the box's RIGHT.
      // Assign them the other way round and every side print comes out mirrored.
      var leftShape = sideShape(THREE, lid, b0, b1, sideD, true);
      var left = laminate(THREE, leftShape,
        sheetUV(THREE, lid, function (x, y) { return [origins[2] + x, -y]; }, box.tileMM), mats, box);
      // Each side wall hangs off a pivot placed on its fold line -- the rear
      // corner where it meets the back panel. Rotating the pivot swings the
      // flap open exactly the way the real one unwraps; rotating the wall
      // itself would swing it about the wrong edge.
      left.rotation.y = -Math.PI / 2;
      left.position.set(0, H / 2, 0);
      var leftPivot = new THREE.Group();
      leftPivot.position.set(-W / 2 + box.wallOuter, 0, -D / 2 + box.wall);
      leftPivot.add(left);
      g.add(leftPivot);

      // RIGHT wall: outward is +X. rotateY(+90) sends local +Z to +X and local
      // +X to -Z, so shape-x runs front-to-back with the notches at x = 0.
      var rightShape = sideShape(THREE, lid, b0, b1, sideD, false);
      var right = laminate(THREE, rightShape,
        sheetUV(THREE, lid, function (x, y) { return [origins[0] + x, -y]; }, box.tileMM), mats, box);
      right.rotation.y = Math.PI / 2;
      right.position.set(0, H / 2, sideD);
      var rightPivot = new THREE.Group();
      rightPivot.position.set(W / 2 - box.wallOuter, 0, -D / 2 + box.wall);
      rightPivot.add(right);
      g.add(rightPivot);

      // BACK wall: outward is -Z. rotateY(180) sends local +X to -X, so
      // shape-x is measured from the box's right-hand side.
      // The back's PRINTED skin runs the full width, past the side walls, so
      // the print carries around the corner the way a bent sheet does. Butt it
      // exactly to the side walls and their linen backing shows as a pale strip
      // down each rear corner -- which is what the seam lines were. A 0.3mm
      // inset keeps its side faces off the side walls' planes so nothing
      // z-fights. Only the skin is oversized; the linen still sits between the
      // walls, where it belongs.
      var skinInset = 0.3;
      var skinW = W - 2 * skinInset;
      var backSkin = new THREE.Mesh(
        bevelledExtrude(THREE, rectShape(THREE, skinW, b0, b1), box.wallOuter,
          Math.min(box.edgeRoll, box.wallOuter * 0.42),
          sheetUV(THREE, lid, function (x, y) { return [origins[1] + skinInset + x, -y]; }, box.tileMM)),
        [mats.art, mats.blackEdge]);
      backSkin.rotation.y = Math.PI;
      backSkin.position.set(W / 2 - skinInset, H / 2, -D / 2 + box.wallOuter);
      g.add(backSkin);

      var backLining = new THREE.Mesh(
        bevelledExtrude(THREE, rectShape(THREE, backW, b0, b1), box.wallInner,
          Math.min(box.edgeRoll, box.wallOuter * 0.42) * 0.55, null),
        [mats.linen, mats.linenEdge]);
      backLining.rotation.y = Math.PI;
      backLining.position.set(W / 2 - box.wall, H / 2, -D / 2 + box.wall);
      g.add(backLining);
      g.userData.flaps = { left: leftPivot, right: rightPivot };
      return g;
    }

    // The cover runs the full height in one piece -- both notches sit on it,
    // and there is no lid line to break it.
    var shell = coverPiece(0, H);
    root.add(shell);

    // Everything that is not the cover lives in its own group, so the body can
    // turn while the lid hangs still in space during the mount-side flip.
    var body = new THREE.Group();
    root.add(body);

    // Everything inside sits within the walls rather than crossing them, for
    // the same reason: no shared face planes, no stipple.
    var innerWFull = W - 2 * box.wall;      // between the side walls
    var innerDFull = D - box.wall;          // in front of the back wall
    var innerZ = box.wall / 2;              // centre of that span

    /**
     * The body's own side skins, one full-height panel each (2026-09-18).
     * Without them the sides read as the raw interior stack -- upper tray,
     * bright divider edge, lower tray -- the moment the cover is off them:
     * through the notch cutouts and, worst, all through the mount-side flip,
     * where the exposed sides showed horizontal division lines. On the real
     * box the chassis is wrapped: one panel from floor to top. The plates,
     * divider and trays are narrowed to end at these skins (butt joints, per
     * the doctrine above), and the skins' outer faces land on the cover
     * walls' inner planes back-to-back, which culling resolves.
     */
    var sideSkin = 1.2;
    var plateW = innerWFull - 2 * sideSkin;
    var skinL = panel(THREE, sideSkin, H, innerDFull, mats.blackEdge, box.tileMM, 0.3);
    skinL.position.set(-(innerWFull - sideSkin) / 2, 0, innerZ);
    var skinR = panel(THREE, sideSkin, H, innerDFull, mats.blackEdge, box.tileMM, 0.3);
    skinR.position.set((innerWFull - sideSkin) / 2, 0, innerZ);
    body.add(skinL, skinR);

    // What shows through the two front seams -- under the fixed top and
    // between the compartments -- is the linen's cut edge, and linen is
    // BRIGHT: each seam read as a white bar across the facade (2026-09-18).
    // On the real box those seams read dark. So each linen slab gives up its
    // front 1.2mm to a black leatherette lip, butt-jointed as ever.
    var faceLip = 1.2;

    // Fixed top. It does not open; only the compartments move.
    var topPlate = panel(THREE, plateW, box.wall, innerDFull, mats.blackEdge);
    topPlate.position.set(0, H / 2 - box.wall / 2, innerZ);
    body.add(topPlate);
    var topLinen = panel(THREE, plateW - 1.2, 1.2, innerDFull - 1.2 - faceLip, mats.linen);
    topLinen.position.set(0, H / 2 - box.wall - 0.6, innerZ - faceLip / 2);
    body.add(topLinen);
    var topLip = panel(THREE, plateW - 1.2, 1.2, faceLip, mats.blackEdge, box.tileMM, 0.3);
    topLip.position.set(0, H / 2 - box.wall - 0.6,
                        innerZ + (innerDFull - 1.2) / 2 - faceLip / 2);
    body.add(topLip);
    // the top is sewn too
    var topStitch = faceStitch(THREE, mats, topPlate, 'y');
    if (topStitch) body.add(topStitch);

    // Floor.
    var floorPlate = panel(THREE, plateW, box.wall, innerDFull, mats.blackEdge);
    floorPlate.position.set(0, -H / 2 + box.wall / 2, innerZ);
    body.add(floorPlate);

    var geo = interior(box);

    // The shelf between the compartments: linen both faces, and what the upper
    // compartment actually rests on. It stops one wall short of the front
    // rather than running out to it -- flush with the opening puts its front
    // edge exactly on the cover's outer surface when the cover is mounted on
    // this face, and that pale edge z-fights through the print as a streak.
    var divider = panel(THREE, plateW, box.divider, innerDFull - box.wall - faceLip, mats.linen);
    divider.position.set(0, geo.dividerY, innerZ - box.wall / 2 - faceLip / 2);
    body.add(divider);
    // its seam-facing lip, flush where the linen used to finish
    var dividerLip = panel(THREE, plateW, box.divider, faceLip, mats.blackEdge, box.tileMM, 0.3);
    dividerLip.position.set(0, geo.dividerY,
                            innerZ - box.wall / 2 + (innerDFull - box.wall) / 2 - faceLip / 2);
    body.add(dividerLip);

    // A back for the body, filling the slab the cover occupies when mounted
    // there. Hidden while the cover covers it -- two solids sharing one slab
    // would z-fight -- and revealed the moment the cover leaves.
    // It runs the FULL height, exactly as the cover's own back panel does, so
    // the back reads flush with the top and the floor instead of stopping at
    // the plates and leaving a step at each end.
    // Barely-radiused corners: the default 1.3mm round leaves pinpricks at the
    // corners of the opening where the lit interior shows through.
    // The cover's rear cut edges land on the very plane this slab's outer face
    // occupies. Coplanar surfaces show through one another at grazing angles,
    // which is the pale linen hairline that used to run up each rear corner and
    // the two dots at the top ones -- the flaps' linen backing, seen edge-on.
    // A fraction of extra thickness makes the slab CAP those edges instead of
    // sharing their plane. 0.35mm is below anything the eye resolves at 157mm.
    var capProud = 0.35;
    var backFill = panel(THREE, W, H, box.wallOuter + capProud,
                         mats.blackEdge, box.tileMM, 0.3);
    backFill.position.set(0, 0, -D / 2 - capProud / 2 + box.wallOuter / 2);
    backFill.visible = false;
    var backFillLiner = panel(THREE, innerWFull, H, box.wallInner,
                              mats.linen, box.tileMM, 0.3);
    backFillLiner.position.set(0, 0, -D / 2 + box.wallOuter + box.wallInner / 2);
    backFillLiner.visible = false;
    body.add(backFill, backFillLiner);
    backFill.userData.liner = backFillLiner;

    // ---- the two compartments, which are also the front facade -------------
    // between the side skins now, with the same scrape clearance as before
    var innerW = innerWFull - 2 * sideSkin - box.drawerClear;
    var innerD = innerDFull - box.drawerClear;
    // seated against the back wall, front finishing just inside the cover edge
    var frontZ = -D / 2 + box.wall + innerD / 2;

    var lower = tray(THREE, innerW, geo.lower, innerD, mats);
    lower.position.set(0, geo.lowerY, frontZ);
    body.add(lower);

    var upper = tray(THREE, innerW, geo.upper, innerD, mats);
    upper.position.set(0, geo.upperY, frontZ);
    body.add(upper);

    return {
      group: root,
      box: box,
      interior: geo,
      parts: {
        shell: shell, body: body, flaps: shell.userData.flaps,
        top: topPlate, divider: divider, backFill: backFill,
        drawers: [lower, upper]
      },
      homeZ: { lower: lower.position.z, upper: upper.position.z }
    };
  }

  /**
   * Leather grain, measured off LDB's product photo rather than invented.
   *
   * The photo gives cells ~0.36mm across with luminance sigma ~0.10, and the
   * structure is cell-and-crease, not scattered blobs: a Voronoi F2-F1 field
   * reproduces that (raised cell interiors, dark creases where cells meet)
   * where random pebbles just read as mush.
   *
   * Returns a normal map AND a roughness map from the same field, because on
   * the real thing the crease bottoms stay matte while the cell tops catch the
   * light -- that broken highlight is most of what says \"leather\" in motion.
   *
   * Everything is sized in millimetres, so callers set repeat from the real
   * dimensions of the surface and the grain comes out the same size on every
   * part regardless of how its UVs happen to be laid out.
   */
  function grainMaps(THREE, opts) {
    var o = opts || {};
    var px = o.px || 512;                 // texture is px x px
    var tileMM = o.tileMM || 24;          // and covers this many mm
    var cellMM = o.cellMM || 0.36;        // measured cell size
    var contrast = o.contrast == null ? 0.10 : o.contrast;

    var perMM = px / tileMM;
    var grid = Math.max(4, Math.round(tileMM / cellMM));   // cells per tile edge
    var cellPx = px / grid;

    // jittered seed grid, wrapping at the tile edges so the texture tiles
    var seed = 1337;
    function rnd() { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; }
    var sx = new Float32Array(grid * grid), sy = new Float32Array(grid * grid);
    for (var gy = 0; gy < grid; gy++) for (var gx = 0; gx < grid; gx++) {
      var k = gy * grid + gx;
      sx[k] = (gx + 0.15 + 0.7 * rnd()) * cellPx;
      sy[k] = (gy + 0.15 + 0.7 * rnd()) * cellPx;
    }

    // F2 - F1 over the tile
    var h = new Float32Array(px * px), hmin = 1e9, hmax = -1e9;
    for (var y = 0; y < px; y++) {
      var cy = Math.floor(y / cellPx);
      for (var x = 0; x < px; x++) {
        var cx = Math.floor(x / cellPx);
        var f1 = 1e9, f2 = 1e9;
        for (var dy = -1; dy <= 1; dy++) for (var dx = -1; dx <= 1; dx++) {
          var ny = ((cy + dy) % grid + grid) % grid;
          var nx = ((cx + dx) % grid + grid) % grid;
          var kk = ny * grid + nx;
          // offset the wrapped seed back into this neighbourhood
          var px2 = sx[kk] + (cx + dx - nx) * cellPx;
          var py2 = sy[kk] + (cy + dy - ny) * cellPx;
          var ddx = x - px2, ddy = y - py2;
          var d2 = ddx * ddx + ddy * ddy;
          if (d2 < f1) { f2 = f1; f1 = d2; } else if (d2 < f2) { f2 = d2; }
        }
        var v = Math.sqrt(f2) - Math.sqrt(f1);
        h[y * px + x] = v;
        if (v < hmin) hmin = v;
        if (v > hmax) hmax = v;
      }
    }
    var span = (hmax - hmin) || 1;
    for (var i2 = 0; i2 < h.length; i2++) {
      // ease it: creases stay sharp, cell interiors flatten off like real grain
      var t = (h[i2] - hmin) / span;
      h[i2] = t * t * (3 - 2 * t);
    }

    function canvasFrom(fn) {
      var c = document.createElement('canvas');
      c.width = c.height = px;
      var g = c.getContext('2d'), img = g.createImageData(px, px);
      fn(img.data);
      g.putImageData(img, 0, 0);
      return c;
    }

    var amp = contrast * 26 * (perMM / 21.3);   // gradient -> normal strength
    var nCanvas = canvasFrom(function (data) {
      for (var y = 0; y < px; y++) for (var x = 0; x < px; x++) {
        var l = h[y * px + ((x - 1 + px) % px)], r = h[y * px + ((x + 1) % px)];
        var u = h[((y - 1 + px) % px) * px + x], d = h[((y + 1) % px) * px + x];
        var nx2 = (l - r) * amp, ny2 = (u - d) * amp;
        var m = Math.sqrt(nx2 * nx2 + ny2 * ny2 + 1), off = (y * px + x) * 4;
        data[off] = (nx2 / m * 0.5 + 0.5) * 255;
        data[off + 1] = (ny2 / m * 0.5 + 0.5) * 255;
        data[off + 2] = (1 / m * 0.5 + 0.5) * 255;
        data[off + 3] = 255;
      }
    });

    var rCanvas = canvasFrom(function (data) {
      for (var i3 = 0; i3 < px * px; i3++) {
        // cell tops slightly glossier than the creases between them
        var v = 255 * (1 - 0.30 * h[i3]);
        var off = i3 * 4;
        data[off] = data[off + 1] = data[off + 2] = v;
        data[off + 3] = 255;
      }
    });

    function tex(canvas, srgb) {
      var t = new THREE.CanvasTexture(canvas);
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.anisotropy = 8;
      return t;
    }
    return { normal: tex(nCanvas), rough: tex(rCanvas), tileMM: tileMM, cellPx: cellPx };
  }

  /** Sets a texture's repeat so one tile covers tileMM across a wMM x hMM surface. */
  function tileFor(texture, wMM, hMM, tileMM) {
    texture.repeat.set(wMM / tileMM, hMM / tileMM);
    return texture;
  }

  return { BOX: BOX, build: build, panelOrigins: panelOrigins,
           grainMaps: grainMaps, tileFor: tileFor, interior: interior };
}));
